import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { UserModel } from '../modules/auth/models/UserModel';

const sentryEnv = {
  enabled: process.env.REACT_APP_SENTRY_ENABLED,
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
  release: process.env.REACT_APP_SENTRY_RELEASE,
  sampleRate: process.env.REACT_APP_SENTRY_SAMPLE_RATE,
  tracesSampleRate: process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
  profilesSampleRate: process.env.REACT_APP_SENTRY_PROFILES_SAMPLE_RATE,
  sendDefaultPii: process.env.REACT_APP_SENTRY_SEND_DEFAULT_PII,
};

function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined) {
    return defaultValue;
  }

  return value === '1' || value.toLowerCase() === 'true';
}

function parseNumber(value: string | undefined): number | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? undefined : parsedValue;
}

// SDK v11 replaced sendDefaultPii with dataCollection, whose defaults collect more
// (cookies, headers, query params and HTTP bodies). Map the old switch onto it so
// REACT_APP_SENTRY_SEND_DEFAULT_PII keeps its v10 meaning. HTTP bodies stay off
// either way: the API carries customer data (IBANs, quotes) that must not leave.
export function dataCollection(sendDefaultPii: boolean): Sentry.BrowserOptions['dataCollection'] {
  return {
    userInfo: sendDefaultPii,
    cookies: sendDefaultPii,
    httpHeaders: { request: sendDefaultPii, response: sendDefaultPii },
    urlQueryParams: sendDefaultPii,
    httpBodies: [],
  };
}

function isSentryEnabled(): boolean {
  return parseBoolean(sentryEnv.enabled, process.env.NODE_ENV === 'production') && !!sentryEnv.dsn;
}

export function initSentry(): void {
  if (!isSentryEnabled()) {
    return;
  }

  const sampleRate = parseNumber(sentryEnv.sampleRate);
  const tracesSampleRate = parseNumber(sentryEnv.tracesSampleRate);
  const profilesSampleRate = parseNumber(sentryEnv.profilesSampleRate);

  const integrations = [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ];

  if (profilesSampleRate !== undefined) {
    integrations.push(Sentry.browserProfilingIntegration());
  }

  Sentry.init({
    dsn: sentryEnv.dsn,
    environment: sentryEnv.environment,
    release: sentryEnv.release,
    enabled: true,
    sampleRate: sampleRate === undefined ? 1.0 : sampleRate,
    tracesSampleRate,
    // SDK v11 dropped transaction-based profiling (profilesSampleRate). The rate now
    // samples whole sessions; 'trace' profiles while a sampled root span is active.
    profileSessionSampleRate: profilesSampleRate,
    profileLifecycle: 'trace',
    dataCollection: dataCollection(parseBoolean(sentryEnv.sendDefaultPii, false)),
    integrations,
  });
}

export function updateSentryUserContext(user?: UserModel): void {
  if (!isSentryEnabled()) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag('employee_id', '');
    return;
  }

  Sentry.setUser({
    id: String(user.id),
    username: user.username,
    email: user.email,
  });

  if (user.employee?.id) {
    Sentry.setTag('employee_id', String(user.employee.id));
  }
}
